/* BrandColors — Firm Branding > Brand Colors tab
   Keys: brand_primary_color, brand_accent_color, brand_enforce_theme
   No new migration needed — keys seeded in Phase 1. */
import { useEffect, useState } from 'react';
import css from '../../../AdminPage.module.css';
import { useFirmBranding, useUpdateFirmBranding } from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';
import { applyFirmBrandOverrides } from '../../../../../hooks/useTheme';

interface Draft {
  brand_primary_color: string;
  brand_accent_color: string;
  brand_enforce_theme: boolean;
}

const DEFAULTS: Draft = {
  brand_primary_color: '#2d9b5e',
  brand_accent_color: '#56b67f',
  brand_enforce_theme: false,
};

function toDraft(branding: Record<string, string | null> | undefined): Draft {
  return {
    brand_primary_color: branding?.brand_primary_color ?? DEFAULTS.brand_primary_color,
    brand_accent_color: branding?.brand_accent_color ?? DEFAULTS.brand_accent_color,
    brand_enforce_theme: branding?.brand_enforce_theme === 'true',
  };
}

function isDirty(a: Draft, b: Draft) {
  return (
    a.brand_primary_color !== b.brand_primary_color ||
    a.brand_accent_color !== b.brand_accent_color ||
    a.brand_enforce_theme !== b.brand_enforce_theme
  );
}

export default function BrandColors() {
  const { data: branding, isLoading } = useFirmBranding();
  const update = useUpdateFirmBranding();
  const { showToast } = useToast();

  const [draft, setDraft] = useState<Draft>(DEFAULTS);
  const [original, setOriginal] = useState<Draft>(DEFAULTS);

  /* Seed form from server data once loaded. */
  useEffect(() => {
    if (!branding) return;
    const d = toDraft(branding);
    setDraft(d);
    setOriginal(d);
  }, [branding]);

  async function handleSave() {
    try {
      await update.mutateAsync([
        { key: 'brand_primary_color', value: draft.brand_primary_color },
        { key: 'brand_accent_color', value: draft.brand_accent_color },
        { key: 'brand_enforce_theme', value: draft.brand_enforce_theme ? 'true' : 'false' },
      ]);
      setOriginal(draft);
      /* Apply / remove the override immediately so the admin sees the effect. */
      applyFirmBrandOverrides(
        draft.brand_primary_color,
        draft.brand_accent_color,
        draft.brand_enforce_theme,
      );
      showToast('Brand colours saved', 'success');
    } catch {
      showToast('Failed to save brand colours', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
      </div>
    );
  }

  const dirty = isDirty(draft, original);

  return (
    <div>
      <p style={{ marginBottom: 20, color: 'var(--text-secondary)' }}>
        Set your firm's primary and accent colours. When "Enforce firm theme" is enabled, these
        colours override each user's personal theme choice across the application.
      </p>

      <div className={css.grid2} style={{ maxWidth: 560, gap: 24, marginBottom: 24 }}>
        <ColorField
          id="input-brand_primary_color"
          label="Primary Colour"
          value={draft.brand_primary_color}
          onChange={(v) => setDraft((p) => ({ ...p, brand_primary_color: v }))}
        />
        <ColorField
          id="input-brand_accent_color"
          label="Accent Colour"
          value={draft.brand_accent_color}
          onChange={(v) => setDraft((p) => ({ ...p, brand_accent_color: v }))}
        />
      </div>

      {/* Enforce toggle */}
      <div className={css.formGroup} style={{ marginBottom: 28 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            id="input-brand_enforce_theme"
            data-testid="input-brand_enforce_theme"
            type="checkbox"
            checked={draft.brand_enforce_theme}
            onChange={(e) => setDraft((p) => ({ ...p, brand_enforce_theme: e.target.checked }))}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <span className={css.formLabel} style={{ margin: 0 }}>
            Enforce firm theme
          </span>
        </label>
        <p
          style={{
            marginTop: 6,
            marginLeft: 26,
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
          }}
        >
          When enabled, the primary colour is applied site-wide for all users, overriding their
          personal theme preference.
        </p>
      </div>

      {/* Live preview */}
      <div
        data-testid="preview-panel"
        style={{
          marginBottom: 24,
          padding: '14px 16px',
          background: 'var(--surface-secondary)',
          borderRadius: 8,
          border: '1px solid var(--border-primary)',
          maxWidth: 560,
        }}
      >
        <p
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: 10,
            color: 'var(--text-secondary)',
          }}
        >
          Preview
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            style={{
              padding: '8px 18px',
              background: draft.brand_primary_color,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'default',
            }}
          >
            Primary
          </button>
          <button
            style={{
              padding: '8px 18px',
              background: draft.brand_accent_color,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'default',
            }}
          >
            Accent
          </button>
          <span style={{ color: draft.brand_primary_color, fontWeight: 500, fontSize: '0.875rem' }}>
            Sample link
          </span>
        </div>
      </div>

      <button
        className={css.btnPrimary}
        data-testid="btn-save"
        onClick={handleSave}
        disabled={!dirty || update.isPending}
      >
        {update.isPending ? 'Saving…' : 'Save Brand Colours'}
      </button>
    </div>
  );
}

/* ── Shared colour picker + hex input ─────────────────────────────────────── */

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className={css.formGroup}>
      <label className={css.formLabel} htmlFor={id}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          id={id}
          data-testid={id}
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 48,
            height: 40,
            padding: 2,
            border: '1px solid var(--border-primary)',
            borderRadius: 6,
            cursor: 'pointer',
            background: 'none',
          }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9a-fA-F]{0,6}$/.test(v)) onChange(v);
          }}
          className={css.formInput}
          style={{ width: 100, fontFamily: 'monospace' }}
          maxLength={7}
          aria-label={`${label} hex value`}
        />
        <span
          data-testid={`swatch-${id}`}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: value,
            border: '2px solid var(--border-primary)',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
